import React, { useState, useRef, useEffect } from 'react';
import { ChatSession, Project } from '../types';
import { SendIcon, BotIcon, PhoneIcon, PlusIcon, MicIcon } from './Icons';

interface SidebarAssistantProps {
  session: ChatSession;
  onSendMessage: (content: string) => void;
  isProcessing: boolean;
  activeProject?: Project | null;
  onNewChat: () => void;
  onToggleVoice: () => void;
  onExpandChat: () => void;
  disableCallAgent?: boolean;
}

export const SidebarAssistant: React.FC<SidebarAssistantProps> = ({
  session,
  onSendMessage,
  isProcessing,
  activeProject,
  onNewChat,
  onToggleVoice,
  onExpandChat,
  disableCallAgent,
}) => {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [session.messages, isProcessing]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isProcessing) return;
    onSendMessage(input);
    setInput('');
  };

  return (
    <div className="flex min-h-0 flex-col h-full rounded-[1.75rem] overflow-hidden relative bdat-soft-panel">
      <div className="px-4 py-3 border-b border-slate-200/80 bg-white/55 flex items-center justify-between z-10 shrink-0">
        <div className="flex items-center gap-2">
          <div className="relative">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-600 to-indigo-500 flex items-center justify-center text-white shadow-[0_12px_24px_rgba(49,95,244,0.18)]">
              <BotIcon />
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border border-white"></div>
          </div>
          <div>
            <h3 className="text-[9px] font-black text-slate-900 uppercase tracking-widest">BDAT Assistent</h3>
            {activeProject?.selectedSkills?.length ? (
              <p className="text-[8px] text-blue-600 font-bold uppercase tracking-[0.18em] mt-0.5">
                {activeProject.selectedSkills.length} skill{activeProject.selectedSkills.length === 1 ? '' : 's'} actief
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onNewChat}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 border border-slate-200 transition-all"
            title="Nieuwe Chat"
          >
            <PlusIcon />
          </button>
          {!disableCallAgent && (
            <button
              onClick={onToggleVoice}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 border border-slate-200 transition-all"
              title="Voice Mode"
            >
              <MicIcon />
            </button>
          )}
          <button
            onClick={onExpandChat}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 border border-slate-200 transition-all"
            title="Vergroot Chat"
          >
            <i className="fas fa-expand text-[11px]"></i>
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 relative overflow-hidden flex flex-col">
        <div className="flex-1 min-h-0 flex flex-col px-4 pt-3 pb-2 overflow-hidden relative">
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar space-y-3 pr-2 pb-1">
            {session.messages.length === 0 && (
              <div className="min-h-full flex flex-col items-center justify-center text-center px-4 py-3">
                <div className="w-11 h-11 bg-slate-100 rounded-full flex items-center justify-center mb-3 text-slate-300 border border-slate-200">
                  <BotIcon />
                </div>
                <p className="text-[9px] text-slate-500 uppercase tracking-[0.15em] font-medium leading-[1.9]">
                  Ik ben je BDAT Assistent.<br />
                  Hoe kan ik je helpen bij <span className="text-slate-800 font-bold">{activeProject?.name || 'deze sessie'}</span>?
                </p>
              </div>
            )}
            {session.messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[90%] p-3 rounded-2xl text-[11px] leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-blue-600 text-white rounded-tr-none shadow-[0_10px_24px_rgba(49,95,244,0.18)]'
                    : 'bg-white/85 text-slate-700 border border-slate-200 rounded-tl-none'
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {isProcessing && (
              <div className="flex justify-start">
                <div className="bg-white/85 border border-slate-200 rounded-2xl rounded-tl-none p-3 px-4">
                  <div className="flex gap-1.5 h-3 items-center">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce [animation-delay:0s]"></div>
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="mt-auto shrink-0 space-y-1.5 pt-1.5 border-t border-slate-200 bg-white/40">
            <div className={`grid gap-2 ${disableCallAgent ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <button
                onClick={() => alert('WhatsApp integratie coming soon...')}
                className="flex items-center justify-center gap-2 py-1.5 bg-white/75 hover:bg-white rounded-lg border border-slate-200 transition-all group"
                title="WhatsApp Integratie"
              >
                <span className="text-slate-400 group-hover:text-[#25D366] transition-colors"><i className="fab fa-whatsapp text-[10px]"></i></span>
                <span className="text-[7px] font-black text-slate-500 uppercase tracking-widest group-hover:text-slate-700 transition-colors">WA Sync</span>
              </button>
              {!disableCallAgent && (
                <button
                  onClick={() => alert('Start Dialpad...')}
                  className="flex items-center justify-center gap-2 py-1.5 bg-white/75 hover:bg-white rounded-lg border border-slate-200 transition-all group"
                  title="Start Test Call"
                >
                  <span className="text-slate-400 group-hover:text-blue-600 transition-colors scale-75"><PhoneIcon /></span>
                  <span className="text-[7px] font-black text-slate-500 uppercase tracking-widest group-hover:text-slate-700 transition-colors">Test Call</span>
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit} className="relative group pb-1">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={isProcessing}
                placeholder="Bericht verzenden..."
                className="w-full bg-white/85 border border-slate-200 rounded-xl px-4 py-2.5 pr-12 text-[10.5px] text-slate-800 outline-none focus:border-blue-400 focus:bg-white transition-all placeholder:text-slate-400"
              />
              <button
                type="submit"
                disabled={isProcessing || !input.trim()}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-blue-600 hover:text-blue-800 disabled:opacity-20 disabled:hover:text-blue-600 transition-all active:scale-90"
                title="Bericht Verzenden"
              >
                <SendIcon />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
